import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { muAddressData } from 'src/environments/constants';
import { environment } from 'src/environments/environment';
import { GeologicServiceClient } from '../../api/ph-web/geologic/geologic.apiServiceClientPb';
import {
  AutocompleteLocationsRequest,
  AutocompleteLocationsResponse,
  GeocodePositionRequest,
  GeocodePositionWithCityRequest,
  GeocodeResponse,
  GetPolygonsRequest,
  GetPolygonsResponse,
  LocationSuggested,
  ResultType,
} from '../../api/ph-web/geologic/geologic.api_pb';
import { AddressGeocodeShared } from '../../models/interface/addressGeocode.interface';
import { AddressGeologicType } from '../../models/interface/addressGeologicType.enum';
import { AddressSuggestedShared } from '../../models/interface/addressSuggested.interface';
import { AddressSuggestedRequestShared } from '../../models/interface/addressSuggestedRequest.interface';
import { CenterShared } from '../../models/interface/center.interface';
import { GrpcFactoryService } from '../../sharedServices/grpc-factory.service';
import { UtilsService } from '../../sharedServices/utils.service';
import { MuException } from '../../types/exception.type';

@Injectable({
  providedIn: 'root',
})
export class MuGeologicService {
  private geologicClient = this.grpcFactoryService.create(
    GeologicServiceClient,
    `${environment.hostname}${environment.hostPort}`,
    'GeologicServiceClient'
  );
  private getPolygonsCall = this.grpcFactoryService.call(this.geologicClient, this.geologicClient.getPolygons, {
    callName: 'getPolygons',
  });
  private getSuggestedListCall = this.grpcFactoryService.call(this.geologicClient, this.geologicClient.autocompleteLocations, {
    callName: 'autocompleteLocations',
  });
  private getAddressByCoordinatesCall = this.grpcFactoryService.call(this.geologicClient, this.geologicClient.reverseGeocode, {
    callName: 'reverseGeocode',
  });
  private getAddressByCoordinatesWithCityCall = this.grpcFactoryService.call(
    this.geologicClient,
    this.geologicClient.reverseGeocodeWithCity,
    {
      callName: 'reverseGeocodeWithCity',
    }
  );

  constructor(
    private grpcFactoryService: GrpcFactoryService,
    private utilsService: UtilsService,
    private translateService: TranslateService
  ) {
    /** */
  }

  /**
   * get Polygons
   *
   * @param {number} cityId
   * @param {number} bussinessLineId
   * @param {number} value
   * @param {string} name
   * @param {boolean} status
   */
  async getPolygon(cityId: number, bussinessLineId: number = 0): Promise<google.maps.Polygon> {
    const request = new GetPolygonsRequest();
    request.setCityid(cityId);
    request.setIdbusinessline(bussinessLineId);
    const response: GetPolygonsResponse = await this.getPolygonsCall(request);
    return response?.getPolygonsList().length > 0
      ? this.utilsService.formatPolygon(response.getPolygonsList()[0].getPolygon())
      : new google.maps.Polygon();
  }

  /**
   * get suggested address list
   * @param {string} countryIso
   * @param {nhumber} userId
   * @param {string} cityGeoName
   * @param {string} address
   * @returns <AddressSuggestedShared[]> response
   */
  async getSuggestedList(addressSuggestedRequest: AddressSuggestedRequestShared): Promise<AddressSuggestedShared[]> {
    const request: AutocompleteLocationsRequest = new AutocompleteLocationsRequest();

    request.setCountryisocode(addressSuggestedRequest.countryIso);
    request.setUserid(addressSuggestedRequest.userId);
    request.setCity(addressSuggestedRequest.countryIso === 'MX' ? addressSuggestedRequest.city.name : addressSuggestedRequest.city.geoName);
    request.setAddress(addressSuggestedRequest.address);

    const response: AutocompleteLocationsResponse = await this.getSuggestedListCall(request);

    return response?.getLocationsuggestedList()?.map((location: LocationSuggested) => {
      const type =
        location.getProvider() === muAddressData.favoriteProvider ? AddressGeologicType.FAVORITE_OTHER : AddressGeologicType.SUGGESTED;
      return {
        address: location.getAddress(),
        text: this.getAddressText(addressSuggestedRequest.city.name, addressSuggestedRequest.countryIso),
        type,
        iconClass: this.getAddressIcon(type),
        cityId: addressSuggestedRequest.city.id,
      };
    });
  }

  /**
   * get address icon
   * @param {AddressGeologicType} type
   * @returns response
   */
  getAddressIcon(type: AddressGeologicType): string {
    let icon = 'mu-icon mu-icon-';
    switch (type.toString()) {
      case AddressGeologicType.FAVORITE_HOME.toString():
      case AddressGeologicType.FAVORITE_OFFICE.toString():
      case AddressGeologicType.FAVORITE_OTHER.toString():
        icon += 'star';
        break;
      case AddressGeologicType.SUGGESTED.toString():
        icon += 'marker';
        break;
      case AddressGeologicType.CURRENT.toString():
        icon += 'aim';
        break;
    }
    return icon;
  }

  /**
   * get address text
   * @param {string} cityName
   * @param {string} countryIso
   * @returns {string} response
   */
  getAddressText(cityName: string, countryIso: string): string {
    return `${cityName}, ${this.translateService.instant('common.location.country.'.concat(countryIso))}`;
  }

  /**
   * get address by coordinates
   * @param {string} countryGeoName
   * @param {number} latitude
   * @param {number} longitude
   * @returns {AddressGeocodeShared} response
   */
  async getAddressByCoordinates(countryGeoName: string, latitude: number, longitude: number): Promise<AddressGeocodeShared> {
    const request: GeocodePositionRequest = new GeocodePositionRequest();
    request.setCountry(countryGeoName);
    request.setLatitude(latitude);
    request.setLongitude(longitude);

    const response: GeocodeResponse = await this.getAddressByCoordinatesCall(request);

    if (!response.getAddress() || !response.getCity() || response.getResulttype() !== ResultType.FOUND) {
      throw new MuException({});
    }

    return {
      state: response.getState(),
      cityGeoName: response.getCity(),
      locality: response.getLocality(),
      neighborbhood: response.getNeighborhood(),
      address: response.getAddress(),
      postalCode: response.getPostalcode(),
      coordinates: {
        latitude: response.getLocation().getLat(),
        longitude: response.getLocation().getLng(),
      },
    };
  }

  /**
   * get address by coordinates
   * @param {string} countryGeoName
   * @param {number} latitude
   * @param {number} longitude
   * @param {city} city
   * @returns {AddressGeocodeShared} response
   */
  async getAddressByCoordinatesWithCity(
    countryGeoName: string,
    latitude: number,
    longitude: number,
    city: number
  ): Promise<AddressGeocodeShared> {
    const request: GeocodePositionWithCityRequest = new GeocodePositionWithCityRequest();
    request.setCountry(countryGeoName);
    request.setLatitude(latitude);
    request.setLongitude(longitude);
    request.setCityid(city);

    const response: GeocodeResponse = await this.getAddressByCoordinatesWithCityCall(request);

    if (!response.getAddress() || response.getResulttype() !== ResultType.FOUND) {
      throw new MuException({});
    }

    return {
      state: response.getState(),
      cityGeoName: response.getCity(),
      locality: response.getLocality(),
      neighborbhood: response.getNeighborhood(),
      address: response.getAddress(),
      postalCode: response.getPostalcode(),
      coordinates: {
        latitude: response.getLocation().getLat(),
        longitude: response.getLocation().getLng(),
      },
    };
  }
}
